import { Controller } from "@hotwired/stimulus"

// expense-select
// ---------------------------------------------------------------
// Multi-select on the All transactions list, the way iOS Mail does it.
//
//   • Two fingers on the list enter select mode and sweep a range of rows.
//     Sweeping back over rows puts them back how they were before the
//     gesture began, so a sweep is never a one-way toggle.
//   • In select mode a tap toggles a row, one finger dragged from a row's
//     check circle sweeps the same way, and rows stop navigating.
//   • The chrome is the pills on the TOTAL row of expenses/_expense_list:
//     Select alone, or Select all + Done while selecting. The native bar is
//     never touched — it holds one item a side and nothing more fits.
//   • The tray (expenses/_select_tray) rises once anything is selected and
//     offers Delete or Move. Non-modal: the list stays live under it.
//
// Rows arrive from the server as `row` targets carrying data-expense-id;
// Load more appends more and they simply join. Every listener sits on the
// list card (delegation). The card and the pills are targets because the
// expenses_frame swaps them out on search / filter — a new list starts clean.

const OPEN_CURVE  = "transform 460ms cubic-bezier(0.2, 0.8, 0.25, 1)"
const CLOSE_CURVE = "transform 340ms cubic-bezier(0.4, 0, 0.2, 1)"
const ROW_SELECTOR = "[data-expense-select-target='row']"
const CHECK_SELECTOR = "[data-expense-select-check]"

export default class extends Controller {
  static targets = [
    "list", "row", "scrollPad",
    "selectPill", "selectAllPill", "donePill",
    "tray", "count", "moveForm", "moveButton", "moveIdle", "moveBusy", "deleteButton"
  ]
  static values = {
    selectAllLabel: String,
    deselectAllLabel: String,
    deleteUrl: String
  }

  connect() {
    this.mode = false
    this.selected = new Set()
    this.sweep = null
    this.swept = false
    this.trayOpen = false
    // Snapshots must never freeze select mode: rows would come back checked
    // with no tray and no Done (same guard as swipe_actions_controller).
    this.onBeforeCache = () => this.exitMode()
    document.addEventListener("turbo:before-cache", this.onBeforeCache)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.onBeforeCache)
    this.exitMode()
  }

  // ── The list card comes and goes with the frame; listeners follow it ──
  listTargetConnected(list) {
    this.listeners ||= {
      touchstart:    (e) => this.#touchStart(e),
      touchmove:     (e) => this.#touchMove(e),
      touchend:      (e) => this.#touchEnd(e),
      touchcancel:   (e) => this.#touchEnd(e),
      pointerdown:   (e) => this.#pointerDown(e),
      pointermove:   (e) => this.#pointerMove(e),
      pointerup:     (e) => this.#pointerUp(e),
      pointercancel: (e) => this.#pointerUp(e),
      click:         (e) => this.#click(e)
    }
    // touch listeners are non-passive: preventDefault is what stops the
    // two-finger gesture from scrolling or pinch-zooming the page.
    list.addEventListener("touchstart", this.listeners.touchstart, { passive: false })
    list.addEventListener("touchmove", this.listeners.touchmove, { passive: false })
    list.addEventListener("touchend", this.listeners.touchend)
    list.addEventListener("touchcancel", this.listeners.touchcancel)
    list.addEventListener("pointerdown", this.listeners.pointerdown)
    list.addEventListener("pointermove", this.listeners.pointermove)
    list.addEventListener("pointerup", this.listeners.pointerup)
    list.addEventListener("pointercancel", this.listeners.pointercancel)
    list.addEventListener("click", this.listeners.click, true)
  }

  listTargetDisconnected(list) {
    Object.entries(this.listeners || {}).forEach(([type, fn]) => {
      list.removeEventListener(type, fn, type === "click")
    })
    this.exitMode()
  }

  // ── Mode ──────────────────────────────────────────────────────────
  toggleMode() {
    this.mode ? this.exitMode() : this.enterMode()
  }

  enterMode() {
    if (this.mode) return
    this.mode = true
    this.element.dataset.selectMode = "true"
    this.#syncChrome()
    this.#render()
  }

  exitMode() {
    if (!this.mode) return
    this.mode = false
    this.sweep = null
    delete this.element.dataset.selectMode
    this.selected.clear()
    this.rowTargets.forEach((row) => row.classList.remove("is-selected"))
    this.#syncChrome()
    this.#render()
  }

  toggleAll() {
    const rows = this.rowTargets
    const on = this.selected.size !== rows.length
    rows.forEach((row) => this.#set(row, on))
    this.#haptic()
    this.#render()
  }

  // ── Tray actions ──────────────────────────────────────────────────
  budgetPicked() {
    if (this.hasMoveButtonTarget) this.moveButtonTarget.disabled = false
  }

  // The ids ride as hidden fields added at submit time, so the form is
  // always exactly the current selection.
  submitMove(event) {
    if (!this.selected.size || !this.hasMoveFormTarget) {
      event.preventDefault()
      return
    }
    const form = this.moveFormTarget
    form.querySelectorAll("input[name='expense_ids[]']").forEach((input) => input.remove())
    this.selected.forEach((id) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "expense_ids[]"
      input.value = id
      form.appendChild(input)
    })
    if (this.hasMoveButtonTarget) this.moveButtonTarget.classList.add("is-busy", "pointer-events-none")
    if (this.hasMoveIdleTarget) this.moveIdleTarget.style.display = "none"
    if (this.hasMoveBusyTarget) this.moveBusyTarget.style.display = "inline-flex"
  }

  // Runs right before confirm-sheet#open on the same click, so the sheet
  // reads a URL that carries the selection plus the open search / filters
  // (the reload after the delete keeps them). Stimulus reads action params
  // at dispatch, which is what makes stamping the attribute first work.
  stampDeleteUrl(event) {
    const url = new URL(this.deleteUrlValue, window.location.origin)
    new URLSearchParams(window.location.search).forEach((value, key) => url.searchParams.append(key, value))
    url.searchParams.delete("page")
    this.selected.forEach((id) => url.searchParams.append("expense_ids[]", id))
    event.currentTarget.dataset.confirmSheetUrlParam = url.pathname + url.search
  }

  // ── Swipe-to-dismiss (iOS-style drag, copied from expense-filter) ──
  // Dragging the tray down past the threshold leaves select mode entirely —
  // the same thing Done does, and the same thing the filter sheet's × does.
  // Only hiding the tray would leave the selection live underneath, and the
  // next tap would bring it straight back up.
  static DRAG_IGNORE_SELECTOR = "input, textarea, select, button, a, label, [contenteditable], [data-no-drag]"

  // The tray is dragged by its top — the handle pill and the count header —
  // not by the budget rows, which scroll.
  static DRAG_HANDLE_SELECTOR = "[data-drag-handle]"

  dragStart(event) {
    if (!this.trayOpen) return
    if (event.target.closest(this.constructor.DRAG_IGNORE_SELECTOR)) {
      this._isDragging = false
      return
    }
    if (!event.target.closest(this.constructor.DRAG_HANDLE_SELECTOR)) {
      this._isDragging = false
      return
    }
    const touch = event.touches[0]
    this._dragStartY = touch.clientY
    this._dragCurrentY = touch.clientY
    this._dragStartX = touch.clientX
    this._dragCommitted = false
    this._isDragging = true
    this.trayTarget.style.transition = "none"
  }

  dragMove(event) {
    if (!this._isDragging) return

    const touch = event.touches[0]
    this._dragCurrentY = touch.clientY
    const deltaY = this._dragCurrentY - this._dragStartY
    const deltaX = touch.clientX - this._dragStartX

    // Commit to a vertical drag only once a small threshold is crossed.
    // Horizontal-dominant movement releases the gesture.
    if (!this._dragCommitted) {
      if (Math.abs(deltaY) < 6 && Math.abs(deltaX) < 6) return
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        this._isDragging = false
        return
      }
      this._dragCommitted = true
    }

    if (deltaY <= 0) {
      this.trayTarget.style.transform = "translateY(0)"
      return
    }

    // Stop the list underneath from scrolling while we drag the tray down.
    if (event.cancelable) event.preventDefault()

    this.trayTarget.style.transform = `translateY(${deltaY}px)`
  }

  dragEnd() {
    if (!this._isDragging) return
    const wasCommitted = this._dragCommitted
    this._isDragging = false
    this._dragCommitted = false

    if (!wasCommitted) {
      // Was a tap or horizontal swipe — snap back to the open position.
      this.trayTarget.style.transition = ""
      this.trayTarget.style.transform = "translateY(0)"
      return
    }

    const deltaY = this._dragCurrentY - this._dragStartY
    const velocity = deltaY / (window.innerHeight || 1)

    if (deltaY > 100 || velocity > 0.2) {
      this.exitMode()
    } else {
      this.trayTarget.style.transition = "transform 300ms cubic-bezier(0.2, 0.8, 0.25, 1)"
      this.trayTarget.style.transform = "translateY(0)"
    }
  }

  // ── Two-finger sweep (touch) ──────────────────────────────────────
  #touchStart(event) {
    if (event.touches.length !== 2) return
    const row = this.#rowAt(this.#midY(event.touches))
    if (!row) return
    event.preventDefault()
    this.#beginSweep(row, "touch")
  }

  #touchMove(event) {
    if (this.sweep?.source !== "touch" || event.touches.length < 2) return
    event.preventDefault()
    this.#sweepTo(this.#rowAt(this.#midY(event.touches)))
  }

  #touchEnd(event) {
    if (this.sweep?.source === "touch" && event.touches.length < 2) this.#endSweep()
  }

  // ── One finger from a check circle (pointer), tap to toggle ───────
  #pointerDown(event) {
    // A new gesture with no sweep running clears the "was a sweep" flag —
    // on touch a finished sweep never produces a click to clean it up.
    if (!this.sweep) this.swept = false
    if (!this.mode || this.sweep) return
    if (event.pointerType === "touch" && !event.isPrimary) return
    const row = this.#rowOf(event.target)
    if (!row || !event.target.closest(CHECK_SELECTOR)) return
    event.preventDefault()
    event.currentTarget.setPointerCapture?.(event.pointerId)
    this.#beginSweep(row, "pointer")
  }

  #pointerMove(event) {
    if (this.sweep?.source !== "pointer") return
    this.#sweepTo(this.#rowAt(event.clientY))
  }

  #pointerUp() {
    if (this.sweep?.source === "pointer") this.#endSweep()
  }

  #click(event) {
    const row = this.#rowOf(event.target)
    if (!row || !this.mode) return
    event.preventDefault()
    event.stopImmediatePropagation()
    // The sweep that started on this row already set it; the click that
    // follows the same touch must not undo it.
    if (this.swept) { this.swept = false; return }
    this.#set(row, !this.selected.has(row.dataset.expenseId))
    this.#haptic()
    this.#render()
  }

  // ── Sweep model ───────────────────────────────────────────────────
  // Every row between the anchor and the finger takes one state — the
  // opposite of what the anchor row was — and rows the finger has left go
  // back to what they were when the sweep began.
  #beginSweep(row, source) {
    const rows = this.rowTargets
    this.sweep = {
      source,
      anchor: rows.indexOf(row),
      to: !this.selected.has(row.dataset.expenseId),
      before: new Map(rows.map((r) => [r.dataset.expenseId, this.selected.has(r.dataset.expenseId)])),
      last: -1
    }
    this.swept = true
    this.enterMode()
    this.#sweepTo(row)
  }

  #sweepTo(row) {
    if (!this.sweep || !row) return
    const rows = this.rowTargets
    const index = rows.indexOf(row)
    if (index === this.sweep.last) return
    this.sweep.last = index
    const lo = Math.min(this.sweep.anchor, index)
    const hi = Math.max(this.sweep.anchor, index)
    let changed = false
    rows.forEach((r, k) => {
      const want = (k >= lo && k <= hi) ? this.sweep.to : this.sweep.before.get(r.dataset.expenseId)
      changed = this.#set(r, want) || changed
    })
    if (changed) this.#haptic()
    this.#render()
  }

  #endSweep() {
    this.sweep = null
  }

  // ── Selection ─────────────────────────────────────────────────────
  #set(row, on) {
    const id = row.dataset.expenseId
    if (this.selected.has(id) === on) return false
    on ? this.selected.add(id) : this.selected.delete(id)
    row.classList.toggle("is-selected", on)
    if (on) this.#pop(row)
    return true
  }

  #pop(row) {
    const check = row.querySelector(CHECK_SELECTOR)
    if (!check) return
    check.classList.remove("is-pop")
    void check.offsetWidth
    check.classList.add("is-pop")
  }

  // ── Chrome ────────────────────────────────────────────────────────
  // `hidden` attribute, not a class: Tailwind's preflight makes it
  // !important, so it beats the pills' inline-flex.
  #syncChrome() {
    const on = this.mode
    this.selectPillTargets.forEach((pill) => { pill.hidden = on })
    this.selectAllPillTargets.forEach((pill) => { pill.hidden = !on })
    this.donePillTargets.forEach((pill) => { pill.hidden = !on })
  }

  #render() {
    const count = this.selected.size
    if (this.hasCountTarget) this.countTarget.textContent = count
    const all = count > 0 && count === this.rowTargets.length
    this.selectAllPillTargets.forEach((pill) => {
      pill.textContent = all ? this.deselectAllLabelValue : this.selectAllLabelValue
    })
    this.#showTray(this.mode && count > 0)
  }

  #showTray(open) {
    if (!this.hasTrayTarget || open === this.trayOpen) return
    this.trayOpen = open
    const tray = this.trayTarget
    tray.style.transition = open ? OPEN_CURVE : CLOSE_CURVE
    tray.offsetHeight // reflow so the transition runs from where the tray is
    tray.style.transform = open ? "translateY(0)" : "translateY(115%)"
    if (this.hasScrollPadTarget) this.scrollPadTarget.classList.toggle("is-tray-open", open)
    open ? this.#bottomNav?.hide() : this.#bottomNav?.show()
    if (!open) this.#resetMoveForm()
  }

  #resetMoveForm() {
    if (!this.hasMoveFormTarget) return
    this.moveFormTarget.querySelectorAll("input[type='radio']").forEach((input) => { input.checked = false })
    if (this.hasMoveButtonTarget) {
      this.moveButtonTarget.disabled = true
      this.moveButtonTarget.classList.remove("is-busy", "pointer-events-none")
    }
    if (this.hasMoveIdleTarget) this.moveIdleTarget.style.display = ""
    if (this.hasMoveBusyTarget) this.moveBusyTarget.style.display = "none"
  }

  // ── Helpers ───────────────────────────────────────────────────────
  #rowOf(target) {
    return target?.closest?.(ROW_SELECTOR) || null
  }

  #rowAt(y) {
    return this.rowTargets.find((row) => {
      const box = row.getBoundingClientRect()
      return y >= box.top && y <= box.bottom
    }) || null
  }

  #midY(touches) {
    return (touches[0].clientY + touches[1].clientY) / 2
  }

  #haptic() {
    this.application.getControllerForElementAndIdentifier(this.element, "bridge--haptic")?.tap()
  }

  get #bottomNav() {
    const el = document.querySelector("[data-controller~='bridge--bottom-nav']")
    return el ? this.application.getControllerForElementAndIdentifier(el, "bridge--bottom-nav") : null
  }
}
